import { useState, useCallback, useRef } from "react";
import { analyzeTradingSignal } from "../services/tradingAnalysis";
import {
  STOCK_SEARCH_API_URL,
  BACKEND_API_URL,
  DATABASE_API_URL,
} from "../apiConfig";

// Moved outside the hook to ensure stability and prevent re-creation on every render
const processStockDataBatch = async (stocks) => {
  try {
    const symbolsWithSuffix = stocks.map((s) => `${s.symbol}.NS`);

    const [historyResponse, priceResponse] = await Promise.all([
      fetch(`${BACKEND_API_URL}/api/upstox/historical`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbols: symbolsWithSuffix }),
      }),
      fetch(`${BACKEND_API_URL}/api/upstox/prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbols: symbolsWithSuffix }),
      }),
    ]);

    if (!historyResponse.ok) {
      const err = await historyResponse.json();
      throw new Error(
        `Failed to fetch historical data: ${err.error || "Unknown error"}`,
      );
    }
    if (!priceResponse.ok) {
      const err = await priceResponse.json();
      throw new Error(
        `Failed to fetch current prices: ${err.error || "Unknown error"}`,
      );
    }

    const [historyResult, priceResult] = await Promise.all([
      historyResponse.json(),
      priceResponse.json(),
    ]);

    const results = stocks.map((stock) => {
      const symbolWithSuffix = `${stock.symbol}.NS`;
      const priceData = priceResult.data.find((p) => p.symbol === symbolWithSuffix);
      const currentPrice = priceData && priceData.price != null ? priceData.price : null;
      if (currentPrice == null) {
        console.warn(`No current price found for ${stock.symbol} - continuing with null price`);
      }

      const historyData = {
        source: historyResult.source,
        data: historyResult.data.filter((d) => d.symbol === symbolWithSuffix),
      };

      const sortedData = historyData.data
        .map((entry) => {
          const open = parseFloat(entry.open ?? entry.low);
          const high = parseFloat(entry.high);
          const low = parseFloat(entry.low);
          const close = parseFloat(entry.close ?? entry.high);

          if (isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
            return null;
          }
          if (
            high < low ||
            high < open ||
            high < close ||
            low > open ||
            low > close
          ) {
            return null;
          }

          return {
            timestamp: new Date(entry.date).getTime(),
            open,
            high,
            low,
            close,
          };
        })
        .filter((item) => item !== null)
        .sort((a, b) => a.timestamp - b.timestamp);

      if (sortedData.length === 0) {
        console.warn(`No processable historical data for ${stock.symbol} - returning partial result`);
      }

      // If we have a valid currentPrice and historical data, run analysis; otherwise return a partial result.
      let signal = 'N/A';
      let predictedBuyRange = null;
      let predictedSellRange = null;

      if (currentPrice != null && sortedData.length > 0) {
        try {
          const analysis = analyzeTradingSignal(sortedData, currentPrice, stock.symbol);
          signal = analysis.signal;
          predictedBuyRange = analysis.predictedBuyRange;
          predictedSellRange = analysis.predictedSellRange;
        } catch (err) {
          console.warn(`Analysis failed for ${stock.symbol}:`, err.message || err);
        }
      }

      return {
        symbol: stock.symbol,
        name: stock.name,
        currentPrice,
        signal,
        historicalData: historyData,
        predictedBuyRange,
        predictedSellRange,
      };
    });

    return results.filter((r) => r !== null);
  } catch (error) {
    console.error(`Error processing batch:`, error.message);
    return [];
  }
};

const hasSavedAnalysis = (stock) => {
  return (
    stock &&
    stock.symbol &&
    stock.currentPrice !== undefined &&
    stock.currentPrice !== null &&
    stock.signal
  );
};

const normalizeWishlistStock = (stock) => ({
  ...stock,
  currentPrice:
    stock.currentPrice !== undefined && stock.currentPrice !== null
      ? Number(stock.currentPrice)
      : stock.currentPrice,
});

export default function useStockWishlistModel() {
  const [state, setState] = useState({
    suggestions: [],
    wishlist: [], // This will hold the user's selected stocks { symbol, name }
    recommendations: [], // This will hold the analyzed wishlist stocks
    isLoading: false,
    error: null,
    selectedStock: null,
    chartData: null,
  });

  const isRequesting = useRef(false);

  const updateState = useCallback(
    (updates) => setState((prev) => ({ ...prev, ...updates })),
    [],
  );

  const searchStocks = useCallback(
    async (query) => {
      // If no query or empty string, fetch all stocks
      const endpoint =
        query && query.trim() !== ""
          ? `${STOCK_SEARCH_API_URL}/search?q=${encodeURIComponent(query)}`
          : `${STOCK_SEARCH_API_URL}/search`;

      try {
        const response = await fetch(endpoint);
        if (!response.ok) {
          throw new Error("Search failed");
        }
        const suggestions = await response.json();
        // Only update suggestions if we're not in a selected state
        if (!state.selectedStock || query !== state.selectedStock.symbol) {
          updateState({ suggestions });
        }
      } catch (error) {
        console.error("Search error:", error);
        updateState({ suggestions: [] });
      }
    },
    [state.selectedStock, updateState],
  );

  const saveWishlist = useCallback(async (wishlistToSave) => {
    if (isRequesting.current) {
      return;
    }
    isRequesting.current = true;
    try {
      const response = await fetch(`${DATABASE_API_URL}/api/wishlist/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          wishlist: wishlistToSave,
          timestamp: new Date().toISOString(),
        }),
      });
      await response.json();
    } catch (error) {
      console.error("Failed to save wishlist:", error);
    } finally {
      isRequesting.current = false;
    }
  }, []);

  const analyzeWishlist = useCallback(
    async (wishlistToAnalyze) => {
      if (isRequesting.current || wishlistToAnalyze.length === 0) return;
      isRequesting.current = true;

      updateState({ isLoading: true, error: null });

      try {
        // Process the wishlist in a single batch
        const analyzedStocks = await processStockDataBatch(wishlistToAnalyze);
        updateState({
          recommendations: analyzedStocks,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        updateState({
          error: error.message,
          isLoading: false,
          recommendations: [],
        });
      } finally {
        isRequesting.current = false;
      }
    },
    [updateState],
  );

  const addToWishlist = useCallback(
    async (stock) => {
      // Prevent adding duplicate stocks
      if (!state.wishlist.find((item) => item.symbol === stock.symbol)) {
        const analyzedStocks = await processStockDataBatch([stock]);
        const stockToAdd =
          analyzedStocks.length > 0 ? analyzedStocks[0] : stock;

        const newWishlist = [...state.wishlist, stockToAdd];
        updateState({
          wishlist: newWishlist,
          recommendations:
            analyzedStocks.length > 0
              ? [...state.recommendations, stockToAdd]
              : state.recommendations,
        });
        await saveWishlist(newWishlist);
        if (analyzedStocks.length === 0) {
          analyzeWishlist(newWishlist);
        }
      }
    },
    [
      state.wishlist,
      state.recommendations,
      updateState,
      saveWishlist,
      analyzeWishlist,
    ],
  );

  const removeFromWishlist = useCallback(
    async (stock) => {
      // Optimistic update: remove from local state immediately
      const newWishlist = state.wishlist.filter(
        (item) => item.symbol !== stock.symbol,
      );
      const newRecommendations = state.recommendations.filter(
        (item) => item.symbol !== stock.symbol,
      );

      if (state.selectedStock?.symbol === stock.symbol) {
        updateState({ selectedStock: null, chartData: null });
      }

      updateState({
        wishlist: newWishlist,
        recommendations: newRecommendations,
      });

      try {
        await fetch(`${DATABASE_API_URL}/api/wishlist/delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ symbol: stock.symbol }),
        });
      } catch (error) {
        console.error("Failed to remove stock from wishlist:", error);
      }
    },
    [state.wishlist, state.recommendations, state.selectedStock, updateState],
  );

  const removeMultipleFromWishlist = useCallback(
    async (stocks) => {
      const symbolsToRemove = stocks.map((stock) => stock.symbol);
      if (symbolsToRemove.length === 0) return;

      const newWishlist = state.wishlist.filter(
        (item) => !symbolsToRemove.includes(item.symbol),
      );
      const newRecommendations = state.recommendations.filter(
        (item) => !symbolsToRemove.includes(item.symbol),
      );

      if (symbolsToRemove.includes(state.selectedStock?.symbol)) {
        updateState({ selectedStock: null, chartData: null });
      }

      updateState({
        wishlist: newWishlist,
        recommendations: newRecommendations,
      });

      try {
        await fetch(`${DATABASE_API_URL}/api/wishlist/delete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ symbols: symbolsToRemove }),
        });
      } catch (error) {
        console.error("Failed to remove multiple stocks from wishlist:", error);
      }
    },
    [state.wishlist, state.recommendations, state.selectedStock, updateState],
  );

  const fetchWatchlist = useCallback(async () => {
    if (isRequesting.current) return;
    isRequesting.current = true;
    updateState({ isLoading: true, error: null });
    try {
      const response = await fetch(`${DATABASE_API_URL}/api/wishlist/fetch`);
      if (!response.ok) {
        const errText = await response.text();
        console.error("fetchWatchlist error response:", errText);
        throw new Error(`Failed to fetch wishlist: ${response.status}`);
      }
      const data = await response.json();
      const wishlist = (data.wishlist || []).map(normalizeWishlistStock);
      const savedRecommendations = wishlist.filter(hasSavedAnalysis);
      updateState({
        wishlist,
        recommendations: savedRecommendations,
      });

      if (wishlist.length > 0) {
        const analyzedStocks = await processStockDataBatch(wishlist);
        if (analyzedStocks.length > 0) {
          updateState({ recommendations: analyzedStocks });
        }
      } else {
        updateState({ recommendations: [] });
      }
    } catch (error) {
      console.error("Failed to fetch wishlist:", error);
      const errorMessage =
        error.message === "Failed to fetch"
          ? "Cannot connect to server. Is the backend running?"
          : error.message;
      updateState({ error: errorMessage });
    } finally {
      isRequesting.current = false;
      updateState({ isLoading: false });
    }
  }, [updateState]);

  const viewStockData = useCallback(
    (stock) => {
      updateState({ selectedStock: stock, chartData: stock.historicalData });
    },
    [updateState],
  );

  const clearSelectedStock = useCallback(() => {
    updateState({ selectedStock: null, chartData: null });
  }, [updateState]);

  return {
    state,
    updateState,
    searchStocks,
    addToWishlist,
    analyzeWishlist, // Expose this function
    viewStockData,
    clearSelectedStock,
    fetchWatchlist,
    removeFromWishlist,
    removeMultipleFromWishlist,
  };
}
